
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, SearchCode, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);

  const productsItems = [
    { name: "DeepSeek Chat", path: "/chat" },
    { name: "DeepSeek R1", path: "/deepseek-r1" },
    { name: "DeepSeek AI", path: "/deepseek-ai" },
  ];

  const compareItems = [
    { name: "vs ChatGPT", path: "/deepseek-vs-chatgpt" },
    { name: "vs Claude", path: "/deepseek-vs-claude" },
    { name: "vs Gemini", path: "/deepseek-vs-gemini" },
    { name: "vs Perplexity", path: "/deepseek-vs-perplexity" },
    { name: "vs Llama", path: "/deepseek-vs-llama" },
    { name: "vs Mistral", path: "/deepseek-vs-mistral" },
  ];

  const resourcesItems = [
    { name: "Documentation", path: "/docs" },
    { name: "Pricing", path: "/pricing" },
    { name: "Blog", path: "/blog" },
    { name: "What is DeepSeek", path: "/what-is-deepseek-ai" },
    { name: "Is DeepSeek Safe?", path: "/is-deepseek-safe" },
    { name: "About", path: "/about" },
  ];

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://cdnjs.buymeacoffee.com/1.0.0/button.prod.min.js";
    script.setAttribute("data-name", "bmc-button");
    script.setAttribute("data-slug", "deepseekai");
    script.setAttribute("data-color", "#40DCA5");
    script.setAttribute("data-emoji", "🚀");
    script.setAttribute("data-font", "Lato");
    script.setAttribute("data-text", "Fuel the Mission");
    script.setAttribute("data-outline-color", "#000000");
    script.setAttribute("data-font-color", "#ffffff");
    script.setAttribute("data-coffee-color", "#FFDD00");
    document.body.appendChild(script);
  }, []);

  return (
    <nav className="sticky top-0 w-full bg-white z-40 border-b shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 relative">
          <div className="flex-shrink-0">
            <Link 
              to="/" 
              className="flex items-center space-x-2 hover:opacity-90 transition-opacity"
            >
              <SearchCode 
                className="h-8 w-8 text-[#0066FF] animate-[wave_2s_ease-in-out_infinite]" 
                strokeWidth={1.5}
              />
              <span className="text-xl sm:text-2xl font-bold text-[#0066FF]">
                DeepSeek AI
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <Link
              to="/"
              className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-[#0066FF] hover:bg-gray-50 rounded-md transition-colors"
            >
              Home
            </Link>

            {/* Products Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-[#0066FF] hover:bg-gray-50">
                  Products
                  <ChevronDown className="ml-1 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border shadow-lg z-50" align="start">
                {productsItems.map((item) => (
                  <DropdownMenuItem key={item.name} asChild>
                    <Link
                      to={item.path}
                      className="w-full cursor-pointer"
                    >
                      {item.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Compare Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-[#0066FF] hover:bg-gray-50">
                  Compare
                  <ChevronDown className="ml-1 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border shadow-lg z-50" align="start">
                {compareItems.map((item) => (
                  <DropdownMenuItem key={item.name} asChild>
                    <Link
                      to={item.path}
                      className="w-full cursor-pointer"
                    >
                      {item.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Resources Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-[#0066FF] hover:bg-gray-50">
                  Resources
                  <ChevronDown className="ml-1 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border shadow-lg z-50" align="start">
                {resourcesItems.map((item) => (
                  <DropdownMenuItem key={item.name} asChild>
                    <Link
                      to={item.path}
                      className="w-full cursor-pointer"
                    >
                      {item.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/pricing"
              className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-[#0066FF] hover:bg-gray-50 rounded-md transition-colors"
            >
              Pricing
            </Link>
            
            <div className="bmc-button ml-2"></div>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-[#0066FF] hover:bg-gray-50 transition-colors"
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white border-b shadow-lg animate-fadeIn z-50">
            <div className="px-4 py-2 space-y-1">
              <Link
                to="/"
                className="block px-4 py-2 text-base font-medium text-gray-700 hover:text-[#0066FF] hover:bg-gray-50 rounded-md transition-colors"
                onClick={() => setIsOpen(false)}
              >
                Home
              </Link>
              
              {/* Products Section */}
              <div className="px-4 py-2">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Products</p>
                {productsItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    className="block px-2 py-1.5 text-base font-medium text-gray-700 hover:text-[#0066FF] hover:bg-gray-50 rounded-md transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>

              {/* Compare Section */}
              <div className="px-4 py-2">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Compare</p>
                {compareItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    className="block px-2 py-1.5 text-base font-medium text-gray-700 hover:text-[#0066FF] hover:bg-gray-50 rounded-md transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>

              {/* Resources Section */}
              <div className="px-4 py-2">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Resources</p>
                {resourcesItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    className="block px-2 py-1.5 text-base font-medium text-gray-700 hover:text-[#0066FF] hover:bg-gray-50 rounded-md transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>

              <Link
                to="/pricing"
                className="block px-4 py-2 text-base font-medium text-gray-700 hover:text-[#0066FF] hover:bg-gray-50 rounded-md transition-colors"
                onClick={() => setIsOpen(false)}
              >
                Pricing
              </Link>
              
              <div className="bmc-button px-4 py-2"></div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
